import type { PlanCategory } from "utils/planUtils";
import type { CollectorType } from "providers/HostedScrapingProvider/types";
import { isAsyncUrlsLlmProject, isAsyncUrlsProject } from "sdecontent";

/**
 * Legacy-rate logic for ENG-1112 (credit-cost transparency for grandfathered users).
 *
 * Accounts that were paying before the multiplier cut-off keep the old price on
 * Akamai-protected domains and eBay raw HTML. The API tells us that through the
 * per-domain quote endpoint (`GET /multiplier/:domain`), which returns both the
 * account's own row (`multipliers`) and the new-user row (`standardMultipliers`).
 * Everything here is pure so the surfaces (API Playground, DataPipeline, Crawler,
 * billing) share one definition of "legacy rate" and one row mapping.
 */

/** Shape of `GET /multiplier/:domain`. `standardMultipliers` is absent on older API builds. */
export type DomainMultiplierResponse = {
  domain: string;
  tier: string;
  /** True only when the Akamai rule prices this account's quote. False for grandfathered accounts, so it does not identify Akamai domains. */
  akamaiPricing?: boolean;
  /** What this account is billed, keyed by the six row labels. */
  multipliers: Record<string, number>;
  /** What a new sign-up pays, same keys. */
  standardMultipliers?: Record<string, number>;
};

/** The request options that pick a row in the quote. */
export type LegacyRateOptions = {
  render?: boolean;
  premium?: boolean;
  ultraPremium?: boolean;
  /** Parsed output (JSON/CSV). Billed on its own tier, which the quote does not cover. */
  autoparse?: boolean;
};

/**
 * Reads the three pricing options off a project's apiParams (the camelCase keys
 * the hosted-scraping forms use), so the surfaces share one translation.
 */
export function legacyRateOptionsFromApiParams(apiParams: Record<string, unknown> | null | undefined): LegacyRateOptions {
  return {
    render: Boolean(apiParams?.render),
    premium: Boolean(apiParams?.premium),
    ultraPremium: Boolean(apiParams?.ultraPremium),
    // DataPipeline stores autoparse; the Playground only stores the format and sets autoparse when it builds the request.
    autoparse: Boolean(apiParams?.autoparse) || apiParams?.outputFormat === "json" || apiParams?.outputFormat === "csv",
  };
}

/**
 * Only URL projects (plain and LLM-ready) have a domain to quote; both are
 * billed on the per-domain rows. SDEs have their own documented prices.
 */
export function isQuotableProject(type: CollectorType): boolean {
  return isAsyncUrlsProject(type) || isAsyncUrlsLlmProject(type);
}

/**
 * Maps request options to the quote row the API bills them on. `ultra_premium`
 * takes precedence over `premium` when both are set, as it does in the API.
 */
export function multiplierRowFor(options: LegacyRateOptions): string {
  const tier = options.ultraPremium ? "Ultra Premium" : options.premium ? "Premium" : "Standard";
  return options.render ? `${tier} (Rendered)` : tier;
}

/**
 * Upper bound on domains quoted for one input. DataPipeline inputs can hold
 * thousands of URLs; quoting more than this buys nothing for a warning message
 * and would hammer the endpoint on every keystroke.
 */
export const MAX_QUOTED_DOMAINS = 20;

/**
 * Takes the domain the quote endpoint wants from one typed URL, or null when
 * the line is not an http(s) URL with a real-looking hostname. Mirrors what
 * DomainMultiplierModal sends: hostname, lowercase, leading `www.` dropped.
 */
export function quoteDomainFromUrl(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  let hostname: string;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    hostname = url.hostname.toLowerCase();
  } catch {
    return null;
  }
  // Bare hostnames ("localhost") and IPs are not quotable domains.
  if (!/\.[a-z]{2,}$/i.test(hostname)) return null;
  return hostname.replace(/^www\./, "");
}

/**
 * Unique quotable domains from a newline-separated URL input, in first-seen
 * order, capped at `cap` (see MAX_QUOTED_DOMAINS).
 */
export function extractQuoteDomains(input: string | undefined, cap: number = MAX_QUOTED_DOMAINS): string[] {
  if (!input) return [];
  const domains: string[] = [];
  for (const line of input.split("\n")) {
    const domain = quoteDomainFromUrl(line);
    if (domain && !domains.includes(domain)) {
      domains.push(domain);
      if (domains.length >= cap) break;
    }
  }
  return domains;
}

/**
 * The legacy-rate condition: the account's row is lower than the standard row
 * for the same options. Returns the standard price (the N to show), or null
 * when the account pays the standard price or the quote cannot be compared.
 * This is exactly the comparison billing makes, so it cannot drift from the
 * real charge, and it covers Akamai domains and eBay alike.
 */
export function legacyRateFromQuote(
  quote: DomainMultiplierResponse | null | undefined,
  options: LegacyRateOptions
): number | null {
  if (!quote?.multipliers || !quote.standardMultipliers) return null;
  // The quote prices raw HTML. A parsed request is billed on its own tier (eBay: ecommerce for
  // every account), so the comparison would warn about a price the account already pays.
  if (options.autoparse) return null;
  const row = multiplierRowFor(options);
  const own = quote.multipliers[row];
  const standard = quote.standardMultipliers[row];
  if (!Number.isFinite(own) || !Number.isFinite(standard)) return null;
  return own < standard ? standard : null;
}

/**
 * Collapses per-domain results into the one number to show. Several legacy
 * domains with different standard prices give one message with the highest N.
 */
export function highestLegacyRate(rates: Array<number | null | undefined>): number | undefined {
  const numbers = rates.filter((rate): rate is number => typeof rate === "number");
  return numbers.length ? Math.max(...numbers) : undefined;
}

/**
 * Billing-page condition. Grandfathered means a paid plan with
 * `newMultipliersEnabled === false`. Free plans are never grandfathered, and
 * `undefined` means the record has not been migrated yet (or the server does
 * not expose the field yet), which is not "legacy", so nothing is shown.
 */
export function isGrandfatheredPlan(
  newMultipliersEnabled: boolean | undefined,
  planCategory: PlanCategory
): boolean {
  if (newMultipliersEnabled !== false) return false;
  return planCategory !== "free" && planCategory !== "free-trial";
}

/**
 * The only hard-coded prices in this feature: the billing page has no domain to
 * quote, so it states the unrendered standard row for the two affected tiers.
 * Source: scraperapi-api `users/multipliers.js`, the `botblocker` (Akamai) and
 * `ecommerce` (eBay raw HTML) tiers. Keep in sync with those tables.
 */
export const LEGACY_PRICING_STANDARD_RATES = {
  akamai: 10,
  ebayHtml: 5,
} as const;
